/**
 * The decision bar that sits between the felt and your rack.
 *
 * It answers one beginner question - "what am I supposed to do now?" - in
 * words, and offers exactly the buttons the engine says are legal. Nothing
 * here computes legality: it reads `model.legalActions` and submits them back
 * unchanged.
 */

import type { Action } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";

export interface ActionBarProps {
  model: TableModel;
  /** Sentence describing what the table is waiting for. */
  hint: string;
  onSubmit: (action: Action) => void;
  manualDraw?: boolean;
  onHint?: () => void;
}

export function ActionBar({ model, hint, onSubmit, onHint, manualDraw = false }: ActionBarProps) {
  const { t } = useI18n();
  const draw = model.legalActions.find((action) => action.type === "draw");
  const win = model.legalActions.find((action) => action.type === "declare-win");
  const kongs = model.legalActions.filter((action) => action.type === "kong");

  return (
    <section className="actions" data-testid="action-bar" aria-label={t("table.actions")}>
      <p className="actions__hint" data-testid="turn-hint">
        {hint}
      </p>
      <div className="actions__buttons">
        {manualDraw ? (
          <button
            type="button"
            data-action="draw"
            className="button button--primary"
            disabled={!draw}
            onClick={() => draw && onSubmit(draw)}
          >
            {t("table.draw")}
          </button>
        ) : null}
        <button
          type="button"
          data-action="declare-win"
          className="button button--win"
          disabled={!win}
          onClick={() => win && onSubmit(win)}
        >
          {t("table.win")}
        </button>
        {kongs.map((action, index) => (
          <button
            key={index}
            type="button"
            data-action="kong"
            className="button button--claimable"
            onClick={() => onSubmit(action)}
          >
            {t("table.kongFromHand")}
          </button>
        ))}
        {onHint ? (
          <button type="button" className="button button--ghost" onClick={onHint}>
            {t("table.hint")}
          </button>
        ) : null}
      </div>
    </section>
  );
}
